import * as signalR from '@microsoft/signalr'
import {
    CFDClientEvent,
    CFDInvokeAction,
} from 'views/CustomerFacingDisplay/hardwareAgent/CFDClientEvent'
import { CFDSubjects } from 'views/CustomerFacingDisplay/hardwareAgent/CFDSubjects'
import { CFDClientMessage } from 'hooks/useHardwareAgent/CFD/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import Guid from 'utilities/Guid'
import { ISendToPOSMessage } from 'pages/CustomerFacingDisplayPage/reducer/Interface'
import { isUndefined } from 'lodash-es'
import { HubConnectionState } from '@microsoft/signalr'
import cfdToast from '../utils/cfdToast'
import usePOSRequestCaller from 'pages/CustomerFacingDisplayPage/hook/usePosRequestCaller'
import { PosOperationalSettingContract } from 'models'
import { defaultSignalRReconnectionDelay } from 'utilities/timeoutHelper'
const LOOP_TIME_SECOND = 10000
const useCFDHWAgent = (hardwareAgentUri: string) => {
    const [CFDClientHub, setCFDClientHub] = useState<
        signalR.HubConnection | undefined
    >()
    const [isHWOnline, setIsHWOnline] = useState(false)
    const manualRetryConnection = useRef<NodeJS.Timeout | undefined>()
    const [operationalSetting, setOperationalSetting] = useState<
        PosOperationalSettingContract
    >({})
    const { get } = usePOSRequestCaller()

    const clearHWAgent = useCallback(() => {
        CFDClientHub?.stop()
        setCFDClientHub(undefined)
    }, [CFDClientHub])
    const handleSendMessageToPOS = useCallback(
        async (message: ISendToPOSMessage, clientID: string) => {
            try {
                const payload: CFDClientMessage = {
                    ...message,
                    type: message.type,
                    posClientID: clientID,
                    messageID: Guid.newGuid().toString(),
                    payload: isUndefined(message?.payload)
                        ? ''
                        : JSON.stringify(message.payload),
                    timestamp: message.timestamp || new Date().toISOString(),
                }

                await CFDClientHub!.invoke(
                    CFDInvokeAction.SendMessageToPOS,
                    payload,
                )
            } catch (err) {
                console.error('[CFD] Error invoking SendMessageToPOS: ', err)
                cfdToast.error(
                    'Error invoking SendMessageToPOS. See console for details.',
                )
            }
        },
        [CFDClientHub],
    )
    const connectWithPOSUsingCode = useCallback(
        async (code: string, onError?: (message?: string) => void) => {
            try {
                await CFDClientHub!.invoke(
                    CFDInvokeAction.ConnectWithPOSUsingCode,
                    code,
                )
            } catch (err) {
                onError?.('Error to connect the client')
                console.error(
                    '[CFD] Error invoking ConnectWithPOSUsingCode: ',
                    err,
                )
                cfdToast.error('Error to connect the client')
            }
        },

        [CFDClientHub],
    )
    const connectWithPOSUsingToken = useCallback(
        async (token: string, onError?: (message?: string) => void) => {
            try {
                await CFDClientHub!.invoke(
                    CFDInvokeAction.ConnectWithPOSUsingToken,
                    token,
                )
            } catch (err) {
                onError?.('Error to connect the client')
                console.error(
                    '[CFD] Error invoking ConnectWithPOSUsingToken: ',
                    err,
                )
                cfdToast.error('Error to connect the client')
            }
        },

        [CFDClientHub],
    )
    const handleConnectWithPOS = useCallback(
        async (clientID: string, onDisconnect: (message?: string) => void) => {
            try {
                await CFDClientHub!.invoke(
                    CFDInvokeAction.ConnectWithPOS,
                    clientID,
                )
            } catch (err) {
                onDisconnect(
                    'Error invoking ConnectWithPOS. See console for details.',
                )
                console.error('Error invoking ConnectWithPOS: ', err)
                cfdToast.error(
                    'Error invoking ConnectWithPOS. See console for details.',
                )
            }
        },
        [CFDClientHub],
    )

    const handleConnectToHardwareAgent = useCallback(() => {
        if (!hardwareAgentUri) return
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${hardwareAgentUri}/hubs/cfd-client`)
            .configureLogging(signalR.LogLevel.None)
            .withAutomaticReconnect(defaultSignalRReconnectionDelay)
            .build()

        connection.onreconnected((connectionId?: string) => {
            console.debug(`[CFD] reconnected - connection Id = ${connectionId}`)
            cfdToast.success('HW Agent is back to online.')
            setIsHWOnline(true)
        })
        connection.onreconnecting((err?: Error) => {
            cfdToast.error('HW Agent is offline.')
            console.debug('[CFD] HW error', err)
            setIsHWOnline(false)
        })

        // Starts the SignalR connection
        connection
            .start()
            .then(() => {
                setIsHWOnline(true)
                console.debug(
                    `[CFD] connected - connection Id = ${connection.connectionId}`,
                )
                if (connection.state === HubConnectionState.Connected) {
                    console.debug('[CFD] Hardware Agent is connected')
                    setCFDClientHub(connection)
                }

                connection.on(
                    CFDClientEvent.OnReceiveMessage,
                    (data: CFDClientMessage) => {
                        // push message to Shared ReactiveX Subject
                        console.debug('[CFD] OnReceiveMessage', data)

                        CFDSubjects.next({
                            type: CFDClientEvent.OnReceiveMessage,
                            params: data,
                        })
                    },
                )
            })
            .catch((e) => {
                setIsHWOnline(false)
                console.debug('[CFD] HW error', e)
                // this will prevent sending an error log to Sentry, when HWAgent is offline
                // log here if needed...
            })
    }, [hardwareAgentUri])
    const cleanLoopRetry = useCallback(() => {
        if (manualRetryConnection.current)
            clearInterval(manualRetryConnection.current)
    }, [])
    const loopRetryProcess = useCallback(
        (func: () => void) => {
            cleanLoopRetry()
            const attemptToStartConnection = () => {
                func()
            }
            manualRetryConnection.current = setInterval(
                () => attemptToStartConnection(),
                LOOP_TIME_SECOND,
            )
        },
        [cleanLoopRetry],
    )

    useEffect(() => {
        // Manually attempt to reconnect the signalR in case of losing connection whether due to the absence of
        // the HWAgent is disconnected from the network
        if (!CFDClientHub) {
            loopRetryProcess(handleConnectToHardwareAgent)
            return
        } else if (CFDClientHub.state === HubConnectionState.Disconnected) {
            loopRetryProcess(() => {
                CFDClientHub.start().catch((e) => {
                    console.debug('[CFD] HW error', e)
                })
            })
            return
        }
        cleanLoopRetry()

        return () => {
            cleanLoopRetry()
        }
    }, [
        CFDClientHub,
        cleanLoopRetry,
        handleConnectToHardwareAgent,
        loopRetryProcess,
    ])

    const getOperationalSetting = useCallback(async () => {
        try {
            const res = await get<PosOperationalSettingContract>(
                `/location/operational-setting`,
            )

            setOperationalSetting(res?.data ?? {})
        } catch (e) {}
    }, [get])

    useEffect(() => {
        getOperationalSetting()
    }, [getOperationalSetting])

    return {
        CFDClientHub,
        isHWOnline,
        handleConnectWithPOS,
        handleSendMessageToPOS,
        handleConnectToHardwareAgent,
        connectWithPOSUsingCode,
        connectWithPOSUsingToken,
        clearHWAgent,
        operationalSetting,
    }
}
export default useCFDHWAgent
